import "./App.scss";

import { store } from "./redux/store";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";

import AppRoutes from "routes/AppRoutes";
import { directionFor } from "hooks/useDirection";
import Toaster from "components/atoms/Toaster";
import AppModal from "components/templates/AppModal";
import ScrollTop from "components/templates/ScrollTop";
import ErrorBoundary from "components/atoms/ErrorBoundary";
import RegisterAppDispatch from "components/atoms/RegisterAppDispatch";
import PartnerAppChrome from "components/templates/PartnerAppChrome";

import createCache from "@emotion/cache";
import { SnackbarProvider } from "notistack";
import { CacheProvider } from "@emotion/react";
import { LocalizationProvider } from "@mui/lab";
import dateAdapter from "@mui/lab/AdapterDateFns";
import { ThemeProvider } from "@mui/material/styles";
import NavBar from "components/templates/NavBar";
import rtlPlugin from "stylis-plugin-rtl";
import { prefixer } from "stylis";
import { useTranslation } from "react-i18next";
import { createTheme } from "@mui/material";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { initWebviewConfig, getWebviewConfig } from "utils/webviewConfig.util";
// import SocialPixels from "socialPixels";

// import ar from "date-fns/locale/ar";
// import en from "date-fns/locale/en-US";
const cache = createCache({ key: "css", prepend: true });

const cacheRtl = createCache({
  key: "muirtl",
  //@ts-ignore
  stylisPlugins: [prefixer, rtlPlugin],
});

declare module "@mui/material/styles" {
  interface Theme {
    status: {
      success: object;
      warning: object;
    };
    customColor: {
      secondary: object;
    };
  }
  interface ThemeOptions {
    status?: {
      success?: object;
      warning?: object;
    };
    customColor: {
      secondary: object;
    };
  }
}

// const stripePromise = loadStripe(config.STRIPE_KEY);

export default function App() {
  const { i18n } = useTranslation();
  const [webviewConfig] = useState(() => initWebviewConfig());

  const theme = useMemo(() => createTheme({
    status: {
      success: {
        color: "rgba(84, 214, 44, 0.16)",
        textContrast: "rgb(34, 154, 22)",
      },
      warning: {
        color: "rgba(255, 193, 7, 0.16)",
        textContrast: "rgb(183, 129, 3)",
      },
    },
    customColor: {
      secondary: {
        color: "rgba(0, 0, 0, 0.53)",
      },
    },
    palette: {
      primary: {
        light: webviewConfig.primaryColor || "#520a76",
        main: webviewConfig.primaryColor || "#520a76",
        dark: webviewConfig.primaryColor || "#520a76",
        contrastText: "#FFFFFF",
      },
      secondary: {
        main: webviewConfig.secondaryColor || "#520A76",
        light: webviewConfig.secondaryColor || "#520A76",
        dark: webviewConfig.secondaryColor || "#520A76",
        contrastText: "#FFFFFF",
      },
      success: {
        main: "rgb(34, 154, 22)",
        light: "rgba(84, 214, 44, 0.16)",
        dark: "#1b5e20",
        // contrastText: "rgb(34, 154, 22)",
      },
      warning: {
        main: "rgb(183, 129, 3)",
        light: "rgba(255, 193, 7, 0.16)",
        dark: "#e65100",
        // contrastText: "rgb(183, 129, 3)"
      },
      error: {
        main: "rgb(183, 33, 54)",
        light: "rgba(255, 72, 66, 0.16)",
        dark: "#c62828",
        // contrastText: "rgb(183, 33, 54)",
      },
      info: {
        main: "#520A76",
        light: "#520A76",
        dark: "#520A76",
        contrastText: "#FFFFFF",
      },
    },
    typography: {
      fontSize: 14,
      fontFamily:
        webviewConfig.fontFamily ||
        (i18n.language === "ar"
          ? '"Cairo", sans-serif, system-ui, -apple-system, BlinkMacSystemFont'
          : '"Inter", system-ui, -apple-system, "Segoe UI", sans-serif'),
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1480,
        xl: 1800,
      },
    },
  }), [i18n.language, webviewConfig]);
  // Get the browser's language
  const browserLang = navigator.language.split("-")[0];
  const defaultLang =
    browserLang === "en" || browserLang === "ar" ? browserLang : "en";
  // const storedLang = localStorage.getItem("lang") || "en";
  const [lang, setLang] = useState(
    /^\/(en|ar)(\/|$)/.exec(window.location.pathname)?.[1] || defaultLang
  );

  useLayoutEffect(() => {
    i18n.changeLanguage(lang);
    localStorage.setItem("lang", lang);
  }, [i18n, lang]);

  /* The document follows i18n, not the route-derived `lang` state.
     Those two fall back differently — `lang` to navigator.language, i18n to
     localStorage — so keying the document off `lang` let html[dir] and the
     rendered copy disagree. Keyed here on i18n.language, the attribute can
     only ever describe the language actually being rendered.
     See hooks/useDirection.ts. */
  useLayoutEffect(() => {
    const active = i18n.language;
    const dir = directionFor(active);
    const htmlElement = document.documentElement;
    htmlElement.setAttribute("dir", dir);
    htmlElement.setAttribute("lang", dir === "rtl" ? "ar" : "en");
    document.body.style.direction = dir;
    document.body.style.fontFamily =
      getWebviewConfig().fontFamily ||
      (dir === "rtl"
        ? '"Cairo", sans-serif, system-ui, -apple-system, BlinkMacSystemFont'
        : '"Inter", system-ui, -apple-system, "Segoe UI", sans-serif');
  }, [i18n.language]);

  useLayoutEffect(() => {
    if (!/^\/(en|ar)/.test(window.location.pathname) && lang) {
      window.location.replace(
        `/${lang}${window.location.pathname}${window.location.search}`
      );
    }
  }, [lang]);

  return (
    <ErrorBoundary>
      {/* <SocialPixels /> */}
      <SnackbarProvider
        maxSnack={5}
        preventDuplicate
        autoHideDuration={2000}
        /* Above the modal backdrop (MUI Modal is 1400, the same layer notistack
           uses), so an error raised from inside a modal — e.g. Update Booking —
           is not hidden behind its own dimmed overlay. */
        classes={{ containerRoot: "yp-toast-stack" }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Toaster />
        <ErrorBoundary>
          {/* <Elements stripe={stripePromise}> */}
          <CacheProvider value={i18n.language === "ar" ? cacheRtl : cache}>
            <ThemeProvider theme={theme}>
              <LocalizationProvider
                // locale={i18n.language === "ar" ? "ar-AR" : "en-EN"}
                dateAdapter={dateAdapter}
              >
                {/* <CssBaseline /> */}
                {/* <ScopedCssBaseline> */}
                <Provider store={store}>
                  <RegisterAppDispatch />
                  <BrowserRouter basename={`/${lang}`}>
                    {/* <ErrorBoundary>
                      <NavBar />
                    </ErrorBoundary> */}
                    {/* <ErrorBoundary>
												<Cookies />
											</ErrorBoundary> */}
                    {/* <ErrorBoundary>
                      <ScrollTop />
                    </ErrorBoundary> */}
                    <ErrorBoundary>
                      <AppModal />
                    </ErrorBoundary>
                    <ErrorBoundary>
                      <AppRoutes />
                    </ErrorBoundary>
                    <PartnerAppChrome />
                  </BrowserRouter>
                </Provider>
                {/* </ScopedCssBaseline> */}
              </LocalizationProvider>
            </ThemeProvider>
          </CacheProvider>
          {/* </Elements> */}
        </ErrorBoundary>
      </SnackbarProvider>
    </ErrorBoundary>
  );
}
